import { z } from 'zod';

/**
 * Campaign-level "pending auto-ratify" policy (I.A.5 / #97;
 * `07-session-experience.md` §8.2).
 *
 * The policy decides what the substrate does with `pending` facts at /
 * after session close. The emitter side (player-authored drafts that land
 * as `pending`) ships in Phase 2 (`07 §7`) — this substrate exists today
 * so the Phase-2 pull-forward doesn't need a migration.
 *
 * - `manual` — default. Pending facts stay pending; the GM must act
 *   explicitly (ratify → canon, or reject → retracted) via the cleanup
 *   inbox. No automatic transitions.
 * - `auto_ratify` — eligible pending facts (per the Phase-2 allowlist,
 *   `07 §8.6`) auto-promote to `canon` at session close, surfacing a
 *   one-step "undo" on the GM session HUD.
 * - `appear_as_pending` — pending stays pending past session close,
 *   visible to participants with the "GM cleanup pending" chip until
 *   the GM acts. Functionally identical to `manual` for steady-state
 *   visibility; distinct so future UX (Phase 2) can differentiate
 *   "I'll clean this up later this week" from "I might never clean this
 *   up and that's fine".
 *
 * The column lives on the Campaign (D1 `campaigns.pending_auto_ratify_policy`).
 */
export const PendingAutoRatifyPolicySchema = z.enum([
  'manual',
  'auto_ratify',
  'appear_as_pending',
]);
export type PendingAutoRatifyPolicy = z.infer<typeof PendingAutoRatifyPolicySchema>;

/** Default policy applied to a freshly-created Campaign. */
export const DEFAULT_PENDING_AUTO_RATIFY_POLICY: PendingAutoRatifyPolicy = 'manual';

/**
 * Per-Campaign auditor rule severity overrides (`04-llm-role.md §5.2.1`). The
 * GM tunes advisory noise per rule: downgrade (`warning → info`), mute
 * (`→ off`), or promote (`info → warning`). The value mirrors `@dl/auditor`'s
 * `SeverityOverride` (kept as a string union here so `@dl/shared` stays free of
 * an `@dl/auditor` dependency — the engine consumes the same shape). The
 * engine clamps `blocking_error` un-overridable regardless of the stored
 * value, so a GM cannot mute a structural-integrity rule.
 *
 * Stored as a JSON object on `campaigns.auditor_rule_overrides`; `{}` = engine
 * defaults. Keys are rule ids (`ref.dangling`, `belief.contradicts_canon`, …).
 */
export const SeverityOverrideSchema = z.enum([
  'blocking_error',
  'error',
  'warning',
  'info',
  'off',
]);
export type SeverityOverrideValue = z.infer<typeof SeverityOverrideSchema>;

export const AuditorRuleOverridesSchema = z.record(z.string().min(1), SeverityOverrideSchema);
export type AuditorRuleOverrides = z.infer<typeof AuditorRuleOverridesSchema>;

/** Default overrides for a freshly-created Campaign — none (engine defaults). */
export const DEFAULT_AUDITOR_RULE_OVERRIDES: AuditorRuleOverrides = {};

/**
 * Parse a stored `auditor_rule_overrides` JSON string into a validated record.
 * Tolerant: malformed JSON or a non-conforming shape collapses to `{}` (engine
 * defaults) rather than throwing — a corrupt override row must never break the
 * audit. Unknown rule ids are kept (forward-compatible: a rule that ships later
 * picks up its stored override without a migration).
 */
export function parseAuditorRuleOverrides(raw: string | null | undefined): AuditorRuleOverrides {
  if (!raw) return {};
  try {
    const parsed = AuditorRuleOverridesSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}
